import { useState, useCallback } from 'react';
import { Check, Droplets, FlaskConical } from 'lucide-react';
import LabBench from '../../LabBench';
import InfoBox from '../../InfoBox';
import { Burette } from '@/components/lab';

const stepInfoContent: Record<number, { title: string; content: string; formulas?: string[] }> = {
  1: {
    title: 'Conditioning the Burette',
    content: 'Rinse the burette 2-3 times with small portions of HCl solution to remove water droplets. Rotate the burette to contact all inner surfaces. Drain through the tip each time. This prevents dilution of the titrant.',
    formulas: ['Rinse volume: 5-10 mL each time', '3 rinses recommended']
  },
  2: {
    title: 'Standardized HCl Solution',
    content: 'The 0.2 M HCl solution is used for this direct titration. This known concentration allows precise calculations. Fill the burette through the top using a funnel, slightly above the zero mark.',
    formulas: ['0.2 M HCl', 'M = mol solute / L solution']
  },
  3: {
    title: 'Removing Air Bubbles',
    content: 'Air bubbles trapped below the stopcock cause volume errors. Open the stopcock rapidly to flush out bubbles. Check the tip against a white background. If bubbles persist, tap the burette gently or refill.',
    formulas: ['Volume error = bubble volume', 'Can cause errors of 0.1-0.5 mL']
  },
  4: {
    title: 'Initial Burette Reading',
    content: 'Record the initial meniscus reading at eye level. Read the bottom of the meniscus to 0.01 mL precision. Use a meniscus reader card for better visibility. Starting near 0.00 mL maximizes the available volume.',
    formulas: ['Precision: +/- 0.01 mL', 'Volume used = Final reading - Initial reading']
  }
};

export default function Part3BuretteSetup() {
  const [rinsed, setRinsed] = useState(false);
  const [filled, setFilled] = useState(false);
  const [primed, setPrimed] = useState(false);
  const [recorded, setRecorded] = useState(false);
  const [buretteVolume, setBuretteVolume] = useState(0);
  const [initialReading, setInitialReading] = useState(0);
  const [currentStep, setCurrentStep] = useState(1);
  const [showInfo, setShowInfo] = useState<number | null>(null);

  const completedSteps = [rinsed, filled, primed, recorded].filter(Boolean).length;

  const handleRinse = useCallback(() => {
    if (!rinsed) {
      setRinsed(true);
      setCurrentStep(2);
    }
  }, [rinsed]);

  const handleFill = useCallback(() => {
    if (rinsed && !filled) {
      setBuretteVolume(50);
      setFilled(true);
      setCurrentStep(3);
    }
  }, [rinsed, filled]);

  const handlePrime = useCallback(() => {
    if (filled && !primed) {
      setBuretteVolume(48);
      setPrimed(true);
      setCurrentStep(4);
    }
  }, [filled, primed]);

  const handleRecord = useCallback(() => {
    if (primed && !recorded) {
      const reading = 0.50 + Math.random() * 0.5;
      setInitialReading(parseFloat(reading.toFixed(2)));
      setRecorded(true);
    }
  }, [primed, recorded]);

  return (
    <div className="experiment-layout">
      <div className="steps-panel">
        <div className="steps-header">
          <FlaskConical size={16} />
          <h3>Part III: Burette Setup</h3>
        </div>
        <div className="steps-list">
          {[
            { id: 1, title: 'Rinse Burette', hint: 'Rinse with HCl solution', done: rinsed },
            { id: 2, title: 'Fill Burette', hint: 'Fill with 0.2 M HCl', done: filled },
            { id: 3, title: 'Prime Stopcock', hint: 'Remove air bubbles', done: primed },
            { id: 4, title: 'Record Initial', hint: 'Read the meniscus level', done: recorded }
          ].map((step) => (
            <div
              key={step.id}
              className={`step-item ${step.done ? 'completed' : ''} ${currentStep === step.id ? 'active' : ''}`}
            >
              <div className={`step-number ${step.done ? 'done' : ''}`}>
                {step.done ? <Check size={12} /> : step.id}
              </div>
              <div className="step-content">
                <span className="step-title">{step.title}</span>
                {currentStep === step.id && !step.done && (
                  <span className="step-hint">{step.hint}</span>
                )}
              </div>
              {stepInfoContent[step.id] && (
                <button
                  className="step-info-btn"
                  onClick={(e) => { e.stopPropagation(); setShowInfo(step.id); }}
                >
                  ?
                </button>
              )}
            </div>
          ))}
        </div>

        {/* Titrant Info */}
        <div className="titrant-info">
          <h4>Titrant Solution</h4>
          <div className="info-row">
            <span>Solution:</span>
            <span className="value">HCl</span>
          </div>
          <div className="info-row">
            <span>Concentration:</span>
            <span className="value">0.2 M</span>
          </div>
          {recorded && (
            <div className="info-row highlight">
              <span>Initial Reading:</span>
              <span className="value">{initialReading.toFixed(2)} mL</span>
            </div>
          )}
        </div>
      </div>

      <LabBench
        experimentName="Burette Setup"
        completedCount={completedSteps}
        totalSteps={4}
        equipment={['50 mL Burette', 'Burette clamp', '0.2 M HCl']}
        labMode="guided"
        currentStep={currentStep}
      >
        <div className="burette-workspace">
          <div className="burette-area">
            <div
              className={`burette-container ${currentStep <= 3 && !primed ? 'clickable' : ''}`}
              onClick={() => {
                if (currentStep === 1) handleRinse();
                else if (currentStep === 2) handleFill();
                else if (currentStep === 3) handlePrime();
              }}
              style={{ cursor: currentStep <= 3 && !primed ? 'pointer' : 'default' }}
            >
              <Burette
                volume={buretteVolume}
                maxVolume={50}
                liquidColor="rgba(200, 255, 200, 0.6)"
                stopcock={primed ? 'closed' : 'open'}
              />
            </div>

            {/* HCl Solution Bottle */}
            <div className="hcl-bottle">
              <svg viewBox="0 0 60 100" width={80} height={120}>
                <rect x="15" y="30" width="30" height="60" rx="3" fill="#5A5A5A" />
                <rect x="20" y="40" width="20" height="45" fill="rgba(200, 255, 200, 0.6)" opacity="0.8" />
                <rect x="18" y="15" width="24" height="15" rx="2" fill="#333" />
                <text x="30" y="65" fontSize="6" fill="#333" textAnchor="middle">HCl</text>
                <text x="30" y="75" fontSize="5" fill="#333" textAnchor="middle">0.2 M</text>
                {/* Hazard symbol */}
                <g transform="translate(25, 83)">
                  <polygon points="5,0 10,9 0,9" fill="#FBBF24" stroke="#000" strokeWidth="0.5" />
                  <text x="5" y="7" fontSize="5" textAnchor="middle" fill="#000">!</text>
                </g>
              </svg>
              <span className="bottle-label">HCl Solution (Caution: Corrosive)</span>
            </div>
          </div>

          {/* Reading Display */}
          {recorded && (
            <div className="reading-display">
              <span className="reading-label">Initial Burette Reading:</span>
              <span className="reading-value">{initialReading.toFixed(2)} mL</span>
            </div>
          )}

          {/* Status Messages */}
          <div className="status-area">
            {!rinsed && (
              <div className="status-message info">
                <Droplets size={20} />
                Click the burette to rinse with HCl solution
              </div>
            )}
            {rinsed && !filled && (
              <div className="status-message info">
                <Droplets size={20} />
                Click to fill the burette with 0.2 M HCl
              </div>
            )}
            {filled && !primed && (
              <div className="status-message info">
                <Droplets size={20} />
                Click to prime the stopcock and remove air bubbles
              </div>
            )}
            {primed && !recorded && (
              <div className="status-message info" onClick={handleRecord} style={{ cursor: 'pointer' }}>
                <FlaskConical size={20} />
                Click here to record the initial burette reading
              </div>
            )}
            {recorded && (
              <div className="status-message success">
                <Check size={20} />
                Burette prepared! Initial reading: {initialReading.toFixed(2)} mL. Ready for titration.
              </div>
            )}
          </div>

          {/* Theory Box */}
          <div className="theory-box">
            <h4>Direct Titration Method</h4>
            <p>
              In this experiment, we use a direct titration. The crushed antacid tablet is
              dissolved in water and titrated directly with 0.2 M HCl to determine the
              acid-neutralizing capacity.
            </p>
            <p>
              The burette must be properly conditioned with HCl to ensure accurate measurements.
              Air bubbles below the stopcock will cause reading errors.
            </p>
          </div>
        </div>

        <style>{`
          .burette-workspace {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            padding: 20px;
            width: 100%;
          }

          .burette-area {
            display: flex;
            gap: 60px;
            align-items: flex-end;
            justify-content: center;
          }

          .burette-container {
            display: flex;
            flex-direction: column;
            align-items: center;
            padding: 10px;
            border-radius: 8px;
            transition: all 0.2s ease;
          }

          .burette-container.clickable {
            cursor: pointer;
          }

          .burette-container.clickable:hover {
            background: rgba(0, 168, 107, 0.1);
          }

          .hcl-bottle {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
          }

          .bottle-label {
            font-size: 11px;
            color: var(--text-secondary);
            max-width: 100px;
            text-align: center;
          }

          .titrant-info {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            margin-top: 16px;
          }

          .titrant-info h4 {
            margin: 0 0 12px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 4px 0;
            font-size: 13px;
          }

          .info-row .value {
            font-family: monospace;
            font-weight: 600;
          }

          .info-row.highlight {
            background: rgba(0, 168, 107, 0.1);
            padding: 8px;
            border-radius: 4px;
            margin-top: 8px;
          }

          .info-row.highlight .value {
            color: var(--secondary);
          }

          .reading-display {
            background: var(--bg-elevated);
            padding: 12px 24px;
            border-radius: 8px;
            display: flex;
            gap: 12px;
            align-items: center;
          }

          .reading-label {
            font-size: 14px;
            color: var(--text-secondary);
          }

          .reading-value {
            font-size: 18px;
            font-weight: 600;
            font-family: 'Courier New', monospace;
            color: var(--secondary);
          }

          .status-area {
            min-height: 50px;
            display: flex;
            justify-content: center;
          }

          .status-message {
            display: flex;
            align-items: center;
            gap: 10px;
            padding: 12px 20px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 500;
          }

          .status-message.info {
            background: rgba(59, 130, 246, 0.2);
            color: #60A5FA;
          }

          .status-message.success {
            background: rgba(0, 168, 107, 0.2);
            color: var(--secondary);
          }

          .theory-box {
            background: var(--bg-elevated);
            border-radius: 8px;
            padding: 16px;
            max-width: 500px;
            border-left: 3px solid var(--secondary);
          }

          .theory-box h4 {
            margin: 0 0 8px 0;
            color: var(--secondary);
            font-size: 14px;
          }

          .theory-box p {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
            line-height: 1.5;
          }

          .theory-box p:last-child {
            margin-bottom: 0;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }
          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>
      </LabBench>

      {showInfo && stepInfoContent[showInfo] && (
        <InfoBox
          step={showInfo}
          experimentId="exp10"
          stepInfo={stepInfoContent[showInfo]}
          onClose={() => setShowInfo(null)}
        />
      )}
    </div>
  );
}
